<template>
    <base-panel
        :panel="panel"
        :review-mode="reviewMode"
        v-on:continue="handleContinue()"
        v-on:submit="handleSubmit()"
        v-on:edit-action="handleEditAction($event)"
        v-on:update-width="handleUpdateWidth($event)"
        v-on:content-shown="initPanel()"
        ref="basePanel"
    >
        <template slot="content">
            <div class="parent d-flex flex-grow-1" :class="{ 'flex-column': panel.layout_style == 'vertical' }">
                <div
                    :data-id="null"
                    ref="choiceContainer"
                    class="d-flex align-items-start mb-4 dropzone flex-wrap"
                    :class="{
                        'flex-column flex-grow-0 horizontal-items-container': panel.layout_style == 'horizontal',
                    }"
                    :style="{ height: choiceContainerHeight }"
                    :data-drop="_uid"
                >
                    <div
                        v-for="choice in choices"
                        :key="choice.id"
                        class="drag-drop no-text-select p-1 py-2 text-center rounded mr-sm-2 mb-2 alert d-flex align-items-center justify-content-center prevent-draggable-drag"
                        :class="{
                            'drag-drop-dragging': choice.isDragging,
                            'bg-white drag-drop-default':
                                (choice.categoryId === null && (!isSubmitted || !showCorrectness)) || choice.isDragging,
                            'alert-primary': choice.categoryId !== null && (!isSubmitted || !showCorrectness),
                            'alert-success':
                                choice.categoryId == choice.correct_category_id && isSubmitted && showCorrectness,
                            'alert-danger drag-drop-incorrect':
                                choice.categoryId != choice.correct_category_id && isSubmitted && showCorrectness,
                            'drag-drop-horizontal': panel.layout_style == 'horizontal',
                            'choice-locked': isDisabled,
                        }"
                        :style="{
                            width: choice.widthStyle || 'auto',
                            height: choice.heightStyle || 'auto',
                        }"
                        :data-id="choice.id"
                        tabindex="0"
                        @keydown.37="dragItem($event, -14, 0)"
                        @keydown.38="dragItem($event, 0, -14)"
                        @keydown.39="dragItem($event, 14, 0)"
                        @keydown.40="dragItem($event, 0, 14)"
                        :data-drag="_uid"
                    >
                        <div
                            class="global-theme-text drag-item-content"
                            :class="{
                                'px-draggable': !isSubmitted && languageCode == 'en',
                                'px-draggable-fr': !isSubmitted && languageCode == 'fr',
                            }"
                        >
                            <span class="choice-text align-middle" :class="{ 'text-secondary': isDisabled }">{{
                                choice.text
                            }}</span>
                            <span
                                v-show="
                                    choice.categoryId == choice.correct_category_id && isSubmitted && showCorrectness
                                "
                                class="icon icon-glyphicons-basic-circle-empty-check align-middle"
                            ></span>
                            <span
                                v-show="
                                    choice.categoryId != choice.correct_category_id && isSubmitted && showCorrectness
                                "
                                class="icon icon-glyphicons-basic-circle-empty-remove align-middle"
                            ></span>
                            <!-- <span
                                v-show="isDisabled"
                                class="icon icon-glyphicons-basic-lock text-secondary align-middle"
                                style="margin-left: 2px"
                            ></span> -->
                            <span
                                v-show="isSubmitted && choice.action == 'show_content_modal' && showCorrectness"
                                @click="selectItem(choice)"
                                @keydown.13="selectItem(choice)"
                                class="badge badge-pill badge-explain align-middle"
                                :class="{
                                    'badge-danger':
                                        choice.categoryId != choice.correct_category_id &&
                                        isSubmitted &&
                                        choice.action == 'show_content_modal',
                                    'badge-success':
                                        choice.categoryId == choice.correct_category_id &&
                                        isSubmitted &&
                                        choice.action == 'show_content_modal',
                                    'pulse-sm-danger':
                                        choice.categoryId != choice.correct_category_id &&
                                        isSubmitted &&
                                        choice.action == 'show_content_modal' &&
                                        !choice.isExplored,
                                    'pulse-sm-success':
                                        choice.categoryId == choice.correct_category_id &&
                                        isSubmitted &&
                                        choice.action == 'show_content_modal' &&
                                        !choice.isExplored,
                                }"
                                style="margin-left: 4px"
                                role="button"
                                tabindex="0"
                                >{{ labelsTranslated.explain }}</span
                            >
                        </div>
                    </div>
                </div>
                <div class="d-flex flex-column flex-grow-1">
                    <div
                        v-for="(category, index) in categories"
                        :key="category.id"
                        class="dropzone no-text-select category-dropzone rounded text-secondary alert global-styles-base"
                        :class="{
                            'alert-primary': category.isDraggedOver,
                            'dropzone-inactive': !category.isDraggedOver,
                            'dropzone-margin': index < categories.length - 1,
                            'mb-1': index >= categories.length - 1 && !(panel.dropzone_size == 'fill'),
                            'dropzone-ready': isMoving,
                            'category-dropzone-sm': panel.dropzone_size == 'small',
                            'category-dropzone-md': panel.dropzone_size == 'medium',
                            'category-dropzone-lg': panel.dropzone_size == 'large',
                            'flex-fill': panel.dropzone_size == 'fill',
                        }"
                        :data-id="category.id"
                        :data-drop="_uid"
                    >
                        {{ category.name }}
                    </div>
                </div>
            </div>
        </template>
        <template slot="modal">
            <!-- Explain Modal -->
            <base-modal :id="'categories-choice-modal' + _uid" :centered="true" :size="choiceModalSize">
                <template slot="title">
                    <p v-show="selectedChoice && selectedChoice.text" class="align-middle mb-0 small font-weight-500">
                        Item: {{ selectedChoice.text }}
                    </p>
                    <p class="mb-0 text-secondary small">
                        {{ labelsTranslated.correctCategory }}:
                        {{ getCategoryNameById(selectedChoice.correct_category_id) }}
                    </p>
                </template>
                <template>
                    <div v-if="selectedChoice.id" class="global-theme-text">
                        <rich-content-display
                            :elements="selectedChoice.elements"
                            :editable="editMode"
                        ></rich-content-display>
                    </div>
                </template>
                <template slot="footer">
                    <button class="btn btn-secondary btn-session" type="button" data-dismiss="modal">
                        {{ labelsTranslated.close }}
                    </button>
                </template>
            </base-modal>
        </template>
    </base-panel>
</template>

<script>
import interact from "interactjs"
import BaseModal from "@/components/BaseModal"
import BasePanel from "@/components/panels/BasePanel"
import RichContentDisplay from "@/components/RichContentDisplay"

export default {
    name: "CategoriesPanel",
    components: {
        BaseModal,
        BasePanel,
        RichContentDisplay,
    },
    props: {
        panel: {
            type: Object,
            required: true,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        locked: {
            type: Boolean,
            required: false,
            default: false,
        },
        reviewMode: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            isMoving: false,
            choices: [],
            categories: [],
            choiceContainerHeight: "auto",
            selectedChoice: {
                text: "",
                elements: [],
            },
            isInitialized: false,
        }
    },
    computed: {
        panelId() {
            return this.panel.id
        },
        isSubmitted() {
            return this.savedResponses && this.savedResponses.length > 0 && this.isInitialized
        },
        correctChoicesCount() {
            return this.choices.filter((choice) => choice.categoryId == choice.correct_category_id).length
        },
        choiceModalSize() {
            if (!this.selectedChoice.modal_width) {
                return ""
            } else if (this.selectedChoice.modal_width == "large") {
                return "large"
            } else if (this.selectedChoice.modal_width == "extra_large") {
                return "xl"
            }
            return ""
        },
        dragSelector() {
            return `[data-drag="${this._uid}"]`
        },
        dropSelector() {
            return `[data-drop="${this._uid}"]`
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        languageCode() {
            return this.$store.getters.sessionLanguageCode
        },
        isDisabled() {
            if (!this.isInitialized) {
                // Don't disable the activity before its been initialized.
                return false
            }
            return this.locked || (this.panel.disable_input_after_submit && this.isSubmitted)
        },
        showCorrectness() {
            return this.panel.show_correctness
        },
        editMode() {
            return this.$store.getters.previewEditMode
        },
    },
    watch: {
        choices(value) {
            this.$emit("update:categoryChoices", value)
        },
        isSubmitted(value) {
            this.$emit("update:isSubmitted", value)
        },
        correctChoicesCount(value) {
            this.$emit("update:correctChoicesCount", value)
        },
        savedResponses() {
            // Freeze the current height of the choice container - this makes sure
            // the container doesn't expand when feedback messages are shown.
            let element = this.$refs.choiceContainer
            this.choiceContainerHeight = element.offsetHeight + "px"
            this.populateSavedResponses()
        },
        isDisabled(value) {
            if (value) {
                interact(this.dropSelector).unset()
            }
        },
    },
    methods: {
        initPanel() {
            if (this.isInitialized) {
                // Make sure the intialization happens only once.
                return
            }
            let vm = this
            this.$nextTick(() => {
                vm.initializeInteract()
                vm.initCategories()
            })
        },
        initCategories() {
            let vm = this
            // Populate choices from those configured on the panel.
            this.choices = this.panel.choices
            // Set properties for vue to watch.
            this.choices.forEach((choice) => {
                vm.$set(choice, "isDragging", false)
                vm.$set(choice, "categoryId", null)
                vm.$set(choice, "correct_category_id", null)
            })
            this.categories = this.panel.choice_categories
            this.categories.forEach((category) => {
                vm.$set(category, "isDraggedOver", false)
            })
            this.$nextTick(() => {
                vm.freezeChoiceWidths()
                vm.populateSavedResponses(true).then(() => {
                    vm.isInitialized = true
                })
            })
        },
        // Store the width of each draggable choice to avoid them getting wider when showing
        // feedback elements after submission. Changing the size of draggable elements causes them
        // to reposition (with interactjs).
        freezeChoiceWidths() {
            let vm = this
            this.choices.forEach((choice) => {
                let element = document.querySelector(`[data-id="${choice.id}"]`)
                vm.$set(choice, "widthStyle", element.offsetWidth + 10 + "px")
                vm.$set(choice, "heightStyle", element.offsetHeight + 2 + "px")
            })
        },
        unfreezeChoiceWidths() {
            this.choices.forEach((choice) => {
                choice.widthStyle = null
                choice.heightStyle = null
            })
        },
        // Populate choices with their correct category ID using data from saved responses.
        // Returns a promise that resolves after items have been positioned properly.
        populateSavedResponses(initPlacement = false) {
            let vm = this
            if (this.savedResponses && this.savedResponses.length) {
                this.savedResponses.forEach((response) => {
                    let choice = vm.choices.find((c) => c.id == response.choice.id)
                    choice.correct_category_id = response.choice.correct_category_id
                    choice.message = response.choice.message
                    choice.elements = response.choice.elements
                    choice.action = response.choice.action
                    if (response.choice_category) {
                        let category = vm.categories.find((c) => c.name == response.choice_category)
                        if (category) {
                            vm.$set(choice, "categoryId", category.id)
                        }
                    } else {
                        choice.categoryId = null
                    }
                    if (initPlacement) {
                        // Position choices where the learner originally placed them.
                        let x = response.position_x
                        let y = response.position_y
                        if (x || y) {
                            let element = document.querySelector(`[data-id="${choice.id}"]`)
                            // Translate the element.
                            element.style.transform = "translate(" + x + "px, " + y + "px)"
                            // Update the position attributes.
                            element.setAttribute("data-x", x)
                            element.setAttribute("data-y", y)
                        }
                    }
                })
                this.$emit("update:isSubmitted", true)
            }
            return new Promise((resolve) => {
                // Trigger an action sequence in interactjs to re-check positions of items.
                // The original activity may have been displayed in a larger parent container,
                // so this means that the positions stored for each item may be outside of the
                // boundaries.
                let interactable = interact(this.dragSelector)
                const drag = { name: "drag", axis: "xy" }
                interactable.reflow(drag).then(() => {
                    resolve()
                })
            })
        },
        initializeInteract() {
            let vm = this
            // Enable choices to be dropped into category dropzones.
            interact(this.dropSelector).dropzone({
                // Only accept elements matching this CSS selector
                accept: this.dragSelector,
                // Require a 75% element overlap for a drop to be possible.
                overlap: 0.8,
                ondragenter: function (event) {
                    let categoryId = event.target.getAttribute("data-id")
                    if (categoryId && !vm.locked) {
                        let category = vm.categories.find((c) => c.id == categoryId)
                        category.isDraggedOver = true
                    }
                },
                ondragleave: function (event) {
                    let categoryId = event.target.getAttribute("data-id")
                    if (categoryId) {
                        let category = vm.categories.find((c) => c.id == categoryId)
                        category.isDraggedOver = false
                    }
                },
                ondrop: function (event) {
                    if (vm.isDisabled) {
                        return
                    }
                    let choiceId = event.relatedTarget.getAttribute("data-id")
                    let choice = vm.choices.find((c) => c.id == choiceId)
                    let categoryId = event.target.getAttribute("data-id")
                    if (categoryId) {
                        let category = vm.categories.find((c) => c.id == categoryId)
                        // Update the category ID on the choice object.
                        choice.categoryId = category.id
                        category.isDraggedOver = false
                        vm.$emit("update:isEdited", true)
                    } else {
                        choice.categoryId = null
                    }
                },
            })

            interact(this.dragSelector).draggable({
                inertia: true,
                modifiers: [
                    interact.modifiers.restrictRect({
                        restriction: ".parent",
                        endOnly: true,
                    }),
                ],
                autoScroll: true,
                listeners: {
                    move(event) {
                        if (vm.isDisabled) {
                            return
                        }
                        var target = event.target
                        // Keep the dragged position in the data-x/data-y attributes.
                        var x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx
                        var y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy

                        // Translate the element.
                        target.style.transform = "translate(" + x + "px, " + y + "px)"

                        // Update the position attributes.
                        target.setAttribute("data-x", x)
                        target.setAttribute("data-y", y)
                    },
                    start(event) {
                        if (vm.isDisabled) {
                            return
                        }
                        vm.isMoving = true
                        let choiceId = event.target.getAttribute("data-id")
                        let choice = vm.choices.find((c) => c.id == choiceId)
                        choice.isDragging = true
                    },
                    end(event) {
                        if (vm.isDisabled) {
                            return
                        }
                        vm.isMoving = false
                        let choiceId = event.target.getAttribute("data-id")
                        let choice = vm.choices.find((c) => c.id == choiceId)
                        choice.isDragging = false
                        vm.updateChoicePositionCoords(
                            choice.id,
                            event.target.getAttribute("data-x"),
                            event.target.getAttribute("data-y")
                        )
                    },
                },
            })
        },
        // Select a choice and perform an action, if configured.
        selectItem(choice) {
            this.$set(choice, "isExplored", true)
            if (choice.action === "show_content_modal") {
                this.selectedChoice = choice
                $("#categories-choice-modal" + this._uid).modal("show")
            }
        },
        // Get a category by its ID.
        getCategoryNameById(id) {
            let category = this.categories.find((c) => c.id == id)
            if (category) {
                return category.name
            }
        },
        // Programmatically move an item by the provided x and y increments.
        dragItem(event, xIncrement, yIncrement) {
            if (this.isDisabled) {
                return
            }
            var target = event.target

            // Keep the dragged position in the data-x/data-y attributes.
            var x = parseFloat(target.getAttribute("data-x")) || 0
            var y = parseFloat(target.getAttribute("data-y")) || 0

            x += xIncrement
            y += yIncrement

            // Translate the element.
            target.style.transform = "translate(" + x + "px, " + y + "px)"

            // Update the position attributes.
            target.setAttribute("data-x", x)
            target.setAttribute("data-y", y)

            // Trigger an action sequence in interactjs to re-check drops. Otherwise,
            // interactjs doesn't know that the item has been dragged into a new dropzone.
            const drag = { name: "drag", axis: "x" }
            interact(this.dragSelector).reflow(drag)

            this.updateChoicePositionCoords(target.getAttribute("data-id"), x, y)
        },
        // Update the x and y position stored on the choice.
        updateChoicePositionCoords(choiceId, xPosition, yPosition) {
            let choice = this.choices.find((c) => c.id == choiceId)
            this.$set(choice, "position_x", Math.round(10 * xPosition) / 10)
            this.$set(choice, "position_y", Math.round(10 * yPosition) / 10)
        },
        // Destroy interact listeners.
        destroyInteract() {
            interact(this.dragSelector).unset()
            interact(this.dropSelector).unset()
        },
        // Reset the display of items and dropzones.
        reset() {
            this.unfreezeChoiceWidths()
            this.destroyInteract()
            this.initializeInteract()
            this.initCategories()
        },
        handleContinue() {
            this.$emit("continue")
        },
        handleSubmit() {
            this.$emit("submit")
        },
        handleEditAction(action) {
            this.$emit("edit-action", action)
        },
        handleUpdateWidth($event) {
            this.$emit("update-width", $event)
        },
        skipVideos() {
            this.$refs.basePanel.skipVideos()
        },
    },
    beforeDestroy() {
        // Destroy the interact listeners so that they're not duplicated.
        this.destroyInteract()
        // Remove event listeners on the choice modal.
        $("#categories-choice-modal" + this._uid).off()
    },
    mounted() {
        // Make sure when the modal is closed, the content is removed. This stops things like
        // audio files and videos from playing the background after the learner has clicked away.
        let vm = this
        $("#categories-choice-modal" + this._uid).on("hidden.bs.modal", function (event) {
            vm.selectedChoice = {
                text: "",
                elements: [],
            }
        })
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/hotspots.scss";

.drag-drop {
    @media (max-width: $breakpoint-sm) {
        max-width: 95%;
    }
    max-width: 48%;
    transition: background-color 0.25s;
    z-index: 5;
    touch-action: none;
}

.drag-drop-horizontal {
    @media (min-width: $breakpoint-md) {
        max-width: 90% !important;
    }
}

.drag-drop-default {
    border: solid $gray-300 $alert-border-width;
    color: $body-color;
}

.drag-drop-dragging {
    z-index: 10 !important;
    box-shadow: $box-shadow-sm;
}

.drag-drop-incorrect {
    z-index: 6 !important;
}

.category-dropzone {
    @media (max-width: $breakpoint-sm) {
        min-height: 200px;
    }
    min-height: 60px;
    z-index: 2;
    padding: 6px;
    border: dashed $secondary 2px;
    font-weight: 500;
    transition: background-color 0.25s, border-color 0.15s;
}

.category-dropzone-sm {
    height: 120px;
}

.category-dropzone-md {
    height: 200px;
}

.category-dropzone-lg {
    height: 300px;
}

.no-text-select {
    -webkit-touch-callout: none;
    user-select: none;
}

.dropzone-margin {
    margin-bottom: 10px;
}

.dropzone-inactive {
    background-color: $gray-100;
}

.choice-locked {
    cursor: initial !important;
}

.categories-info {
    cursor: pointer;
    font-size: 100%;
    transform: scale(1.15);
    margin-left: 2px;
}

.badge-explain {
    margin-left: 1px;
    vertical-align: 2px;
    font-size: 9.5px;
}

.px-draggable {
    padding-left: 36px;
    padding-right: 36px;
}

.px-draggable-fr {
    padding-left: 38px;
    padding-right: 38px;
}

.drag-item-content {
    font-weight: 500;
    font-size: 15px !important;
    line-height: 1.2;
}

.choice-text {
    margin-right: 2px;
}

.horizontal-items-container {
    margin-right: 10px;
    @media (min-width: $breakpoint-md) {
        margin-right: 15px;
    }
    max-width: 47%;
}

.dropzone-ready {
    border-color: #3b77d8;
}
</style>